import type { PresenceContext, Transition, Variants } from "motion/react";
import type { ContextType } from "react";

import { ANIMATED_PAGE_MOTION, noop } from "@ryde/shared";

/**
 * Presence for the page content: keeps `isPresent` (so `useIsPresent` still reports a leaving
 * route) but detaches nested motion components from the route exit. Otherwise a motion child
 * mounted mid-exit registers as "not exited" forever, `AnimatePresence mode="wait"` never
 * mounts the next route and the invisible leaving page keeps blocking clicks.
 */
export const getIsolatedPagePresenceContext = (
  outerContext: ContextType<typeof PresenceContext>,
): ContextType<typeof PresenceContext> => {
  if (!outerContext) {
    return null;
  }

  const isolatedContext: ContextType<typeof PresenceContext> = {
    ...outerContext,
    onExitComplete: undefined,
    register: () => noop,
  };
  return isolatedContext;
};

export const getAnimatedPageVariants = ({
  shouldReduceMotion,
  exitDurationSeconds = ANIMATED_PAGE_MOTION.exitDurationSeconds,
}: {
  shouldReduceMotion: boolean;
  exitDurationSeconds?: number;
}): Variants => {
  if (shouldReduceMotion) {
    const reduced: Variants = {
      initial: { opacity: ANIMATED_PAGE_MOTION.initialOpacity },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    };
    return reduced;
  }

  const variants: Variants = {
    initial: { opacity: ANIMATED_PAGE_MOTION.initialOpacity },
    animate: { opacity: 1 },
    exit: {
      opacity: 0,
      transition: {
        duration: exitDurationSeconds,
        ease: ANIMATED_PAGE_MOTION.exitEase,
      },
    },
  };
  return variants;
};

export const getAnimatedPageTransition = ({
  durationSeconds,
  shouldReduceMotion,
}: {
  durationSeconds: number;
  shouldReduceMotion: boolean;
}): Transition => {
  if (shouldReduceMotion) {
    const reduced: Transition = { duration: 0 };
    return reduced;
  }

  const transition: Transition = {
    duration: durationSeconds,
    ease: ANIMATED_PAGE_MOTION.ease,
  };
  return transition;
};
